import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { AppProvider, AuthProvider } from "@/context/AppContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Quality from "@/pages/Quality";
import Markets from "@/pages/Markets";
import Packaging from "@/pages/Packaging";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import Faq from "@/pages/Faq";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="min-h-screen bg-parchment pt-44 pb-24 text-center" data-testid="not-found-page">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-forest">This page has wandered off the trade route.</h1>
      <a href="/" data-testid="not-found-home-link" className="mt-8 inline-block bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors">
        Return Home
      </a>
    </div>
  );
}

function Layout() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/rice" element={<Products fixedCategory="rice" />} />
          <Route path="/spices" element={<Products fixedCategory="spices" />} />
          <Route path="/tea" element={<Products fixedCategory="tea" />} />
          <Route path="/quality" element={<Quality />} />
          <Route path="/markets" element={<Markets />} />
          <Route path="/packaging" element={<Packaging />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFloat />}
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, autoRaf: true });
    return () => lenis.destroy();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppProvider>
          <AuthProvider>
            <Layout />
          </AuthProvider>
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
